/**
 * Item Form Modal Component
 * Modal form for creating and editing inventory items
 */

import React, { useState, useEffect } from 'react';
import { XCircleIcon, SparklesIcon } from '@heroicons/react/24/outline';
import LocationDropdown from './LocationDropdown';
import axios from 'axios';

const ItemForm = ({
  show,
  isEditing = false,
  formData,
  setFormData,
  onSave,
  onCancel,
  productTypes = [],
  hasUnsavedChanges
}) => {
  const [availableVariants, setAvailableVariants] = useState([]);

  // Fetch variants when product type changes
  const fetchVariantsForProductType = async (productTypeId) => {
    if (!productTypeId) {
      setAvailableVariants([]);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/inventory/product-types/${productTypeId}/variants`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailableVariants(response.data || []);
    } catch (error) {
      console.error('Error fetching variants:', error);
      setAvailableVariants([]);
    }
  };

  // Handle bulk ID generation
  const handleGenerateBulkIds = async (productTypeId, quantity) => {
    if (!productTypeId || quantity < 1) return;

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post('/api/inventory/bulk/generate-ids', {
        productTypeId,
        quantity: parseInt(quantity)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        const generatedIds = response.data.data.suggestions.map(suggestion => suggestion.suggestedId);
        setFormData(prev => ({
          ...prev,
          bulkGeneratedIds: generatedIds
        }));
      }
    } catch (error) {
      console.error('Error generating bulk IDs:', error);
    }
  };

  // Handle bulk quantity change
  const handleBulkQuantityChange = (quantity) => {
    const newQuantity = Math.max(2, Math.min(100, parseInt(quantity) || 2));
    setFormData(prev => ({
      ...prev,
      bulkQuantity: newQuantity
    }));

    // Regenerate IDs if product type is selected
    if (formData.productTypeId) {
      handleGenerateBulkIds(formData.productTypeId, newQuantity);
    }
  };

  // Load variants when product type changes
  useEffect(() => {
    if (show && formData.productTypeId) {
      fetchVariantsForProductType(formData.productTypeId);
    } else {
      setAvailableVariants([]);
    }
  }, [show, formData.productTypeId]);

  if (!show) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const handleProductTypeChange = (productTypeId) => {
    setFormData(prev => ({ 
      ...prev, 
      productTypeId,
      productVariantId: '', // Reset variant selection when product type changes
      bulkGeneratedIds: [] // Clear existing IDs when changing product type
    }));

    // Generate bulk IDs if bulk mode is active
    if (formData.isBulkAdd && productTypeId && (formData.bulkQuantity || 2) > 0) {
      handleGenerateBulkIds(productTypeId, formData.bulkQuantity || 2);
    }
  };

  const selectedProductType = productTypes.find(type => type.id === formData.productTypeId);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-end sm:items-center justify-center min-h-screen pt-4 px-2 sm:px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onCancel}></div>

        {/* Modal panel - Mobile Optimized */}
        <div className="inline-block align-bottom bg-white rounded-t-lg sm:rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full w-full">
          <form onSubmit={handleSubmit} className="flex flex-col">
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              {/* Header - Mobile Optimized */}
              <div className="flex items-center justify-between mb-4 sticky top-0 bg-white pb-2 border-b sm:border-b-0">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900 truncate">
                  {isEditing ? 'Edit Inventory Item' : 'Add New Inventory Item'}
                </h2>
                <button type="button" onClick={onCancel} className="text-gray-400 hover:text-gray-600 p-2 sm:p-1 -m-2 sm:m-0 touch-manipulation" aria-label="Close form">
                  <XCircleIcon className="h-6 w-6 sm:h-5 sm:w-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Product Type *
                  </label>
                  <select
                    value={formData.productTypeId}
                    onChange={(e) => handleProductTypeChange(e.target.value)}
                    className="w-full px-4 py-3 sm:px-3 sm:py-2 text-base sm:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent touch-manipulation"
                    required
                  >
                    <option value="">Select a product type</option>
                    {productTypes.map(type => (
                      <option key={type.id} value={type.id}>
                        {type.name} ({type.category})
                      </option>
                    ))}
                  </select>
                </div>
                
                {/* Variant Selection - Only show if product type has variants */}
                {availableVariants.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Product Variant
                    </label>
                    <select
                      value={formData.productVariantId || ''}
                      onChange={(e) => setFormData({...formData, productVariantId: e.target.value})}
                      className="w-full px-4 py-3 sm:px-3 sm:py-2 text-base sm:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent touch-manipulation"
                    >
                      <option value="">No specific variant</option>
                      {availableVariants.map(variant => {
                        // Format rate type for display
                        const formatRateType = (rateType) => {
                          switch (rateType) {
                            case 'HOURLY': return 'hourly';
                            case 'DAILY': return 'daily';
                            case 'WEEKLY': return 'weekly';
                            case 'MONTHLY': return 'monthly';
                            case 'FLAT_RATE': return 'flat rate';
                            case 'PER_ITEM': return 'per item';
                            case 'PER_PERSON': return 'per person';
                            case 'FIXED': return 'fixed';
                            default: return rateType.toLowerCase();
                          }
                        };

                        // Build rate display for this variant
                        const rateDisplay = variant.rates && variant.rates.length > 0
                          ? ` ($${variant.rates[0].rate}/${formatRateType(variant.rates[0].rateType)})`
                          : '';
                        return (
                          <option key={variant.id} value={variant.id}>
                            {variant.name}{rateDisplay}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                )}
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Item ID *
                  </label>
                  <input
                    type="text"
                    value={formData.isBulkAdd ? `${formData.bulkQuantity || 2} IDs will be auto-generated` : formData.itemNumber}
                    onChange={(e) => setFormData({ ...formData, itemNumber: e.target.value })}
                    className={`w-full px-4 py-3 sm:px-3 sm:py-2 text-base sm:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent touch-manipulation ${
                      formData.isBulkAdd ? 'bg-gray-100 cursor-not-allowed text-gray-500' : ''
                    }`}
                    placeholder={formData.isBulkAdd ? `${formData.bulkQuantity || 2} IDs will be auto-generated` : "e.g., KY-001, BK-001"}
                    required={!formData.isBulkAdd}
                    disabled={formData.isBulkAdd}
                  />
                  {formData.isBulkAdd && (
                    <p className="text-xs text-blue-600 mt-1">
                      Item IDs will be automatically generated based on the product type
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Serial Number
                  </label>
                  <input
                    type="text"
                    value={formData.serialNumber}
                    onChange={(e) => setFormData({ ...formData, serialNumber: e.target.value })}
                    className="w-full px-4 py-3 sm:px-3 sm:py-2 text-base sm:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent touch-manipulation"
                    placeholder="Serial number"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Condition
                  </label>
                  <select
                    value={formData.condition}
                    onChange={(e) => setFormData({ ...formData, condition: e.target.value })}
                    className="w-full px-4 py-3 sm:px-3 sm:py-2 text-base sm:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent touch-manipulation"
                  >
                    <option value="">Select condition...</option>
                    <option value="NEW">New</option>
                    <option value="EXCELLENT">Excellent</option>
                    <option value="GOOD">Good</option>
                    <option value="FAIR">Fair</option>
                    <option value="POOR">Poor</option>
                  </select>
                </div>
                </div>

                <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Location
                </label>
                <LocationDropdown
                  value={formData.location}
                  onChange={(location) => setFormData({...formData, location})}
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="w-full px-4 py-3 sm:px-3 sm:py-2 text-base sm:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent touch-manipulation resize-none sm:resize-y"
                  rows={4}
                  placeholder="Additional notes about this item..."
                />
              </div>

              {/* Bulk Add Mode Toggle - Mobile Optimized */}
              {!isEditing && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between space-y-3 sm:space-y-0">
                    <div className="flex flex-col sm:flex-row sm:items-center">
                      <div className="flex items-center">
                        <svg className="w-5 h-5 text-blue-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                        </svg>
                        <span className="text-sm font-medium text-blue-900">Bulk Add Mode</span>
                      </div>
                      <p className="text-xs text-blue-600 sm:ml-2 mt-1 sm:mt-0">Create multiple items of the same type at once</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.isBulkAdd || false}
                        onChange={(e) => {
                          const isEnabled = e.target.checked;
                          setFormData({
                            ...formData,
                            isBulkAdd: isEnabled,
                            bulkQuantity: isEnabled ? 2 : 1,
                            bulkGeneratedIds: isEnabled ? [] : []
                          });

                          // Generate IDs if enabling bulk mode and product type is selected
                          if (isEnabled && formData.productTypeId) {
                            handleGenerateBulkIds(formData.productTypeId, 2);
                          }
                        }}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                  
                  {formData.isBulkAdd && (
                    <div className="mt-4 pt-4 border-t border-blue-200">
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-blue-700 mb-2">
                            Quantity *
                          </label>
                          <input
                            type="number"
                            min="1"
                            max="100"
                            value={formData.bulkQuantity || 2}
                            onChange={(e) => handleBulkQuantityChange(e.target.value)}
                            className="w-full px-4 py-3 sm:px-3 sm:py-2 text-base sm:text-sm border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white touch-manipulation"
                            placeholder="Number of items to create"
                          />
                          <p className="text-xs text-blue-600 mt-1">Maximum 100 items per batch</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-blue-700 mb-2">
                            Generated IDs Preview
                          </label>
                          <div className="bg-white border border-blue-300 rounded-lg p-3 max-h-24 sm:max-h-20 overflow-y-auto">
                            {formData.bulkGeneratedIds && formData.bulkGeneratedIds.length > 0 ? (
                              <div className="text-xs text-gray-600 space-y-1">
                                {formData.productVariantId && (
                                  <div className="text-xs text-indigo-600 font-medium mb-1 border-b border-indigo-200 pb-1">
                                    Variant: {availableVariants.find(v => v.id === formData.productVariantId)?.name || 'Selected Variant'}
                                  </div>
                                )}
                                {formData.bulkGeneratedIds.slice(0, 5).map((id, index) => (
                                  <div key={index} className="flex items-center">
                                    <SparklesIcon className="h-3 w-3 text-blue-500 mr-1" />
                                    {id}
                                  </div>
                                ))}
                                {formData.bulkGeneratedIds.length > 5 && (
                                  <div className="text-blue-600 font-medium">
                                    +{formData.bulkGeneratedIds.length - 5} more...
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="text-xs text-gray-400 italic">
                                Select a product type to generate IDs
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Form Actions - Mobile Optimized */}
              <div className="bg-gray-50 rounded-lg px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse border-t mt-6">
              <button
                type="submit"
                className="w-full sm:w-auto sm:ml-3 px-4 py-3 sm:py-2 text-base sm:text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
                disabled={formData.isBulkAdd && (!formData.bulkGeneratedIds || formData.bulkGeneratedIds.length === 0)}
              >
                {isEditing ? 'Update Item' : (formData.isBulkAdd ? `Add ${formData.bulkQuantity || 2} Items` : 'Add Item')}
              </button>
              <button
                type="button"
                onClick={onCancel}
                className="w-full sm:w-auto mt-3 sm:mt-0 px-4 py-3 sm:py-2 text-base sm:text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 touch-manipulation"
              >
                Cancel
              </button>
              </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ItemForm;